import { useEffect } from "react"
import { Collapse, Divider, Grid, Skeleton, Typography, useMediaQuery, useTheme, Box } from "@mui/material"
import { ISettingsItem } from "../../../../../Settings"
import { usePlan } from "../../../../../Plans"
import { useForm } from "../../../../../Form"
import { useInitReq } from "../../../../../InitReq"
import BuyViaWppCard from "./BuyViaWppCard"
import PaymentMethods from "./PaymentMethods"
import CheckboxDefault from "../../../../CheckboxDefault"
import { ReactComponent as Checked } from '../../../../../assets/icons/cardChecked.svg'
import { ReactComponent as Unchecked } from '../../../../../assets/icons/cardUnchecked.svg'

interface PaymentStepProps extends ISettingsItem {
  paymentStep: boolean
}

export default function PaymentStep({ name, variables, paymentStep }: PaymentStepProps) {
  const { currentFields: { paymentOption, cardMethodDisabled, paymentMethod }, setModelledField, updateFormState } = useForm()
  const { physicalChipAvailable, virtualStock, loadingPhysical, loadingVirtual, loadingBrandConfig } = useInitReq()
  const { chosenPlan } = usePlan()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))
  const noStock = !physicalChipAvailable && !virtualStock
  const { text = [] } = variables || {}

  const isAnnual = chosenPlan === 'annual'
  const showPlanSelection = isAnnual && !paymentStep
  const showInstallmentSummary = isAnnual && paymentStep && paymentOption === 'installment'
  
  useEffect(() => {
    // No passo de pagamento a compra já foi criada: o método está decidido e o
    // estoque não importa mais. Sem isto, voltar direto a ele (F5) com o
    // estoque ainda carregando trocava o método para DISABLED.
    if (paymentStep) return
    setModelledField('paymentMethod', noStock ? 'DISABLED' : cardMethodDisabled ? 'PIX' : paymentMethod)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[noStock])

  const handlePaymentOptionChange = (value: "oneTime" | "installment") => {
    updateFormState(prevState => ({ ...prevState, paymentOption: value, paymentMethod: 'CARTAO' }))
  }

  return (
    <Grid
      container
      item
      sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', gap: 5, width: '100%' }}
      className={name ? `${name} payment-step` : "payment-step"}
    >
      <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: { xs: '100%', sm: '90%', md: '100%', lg: '80%' } }}>
        {noStock ? (
          (loadingPhysical || loadingVirtual || loadingBrandConfig) ? (
            <Skeleton variant="rectangular" width="100%" height={90} sx={{ borderRadius: '12px' }} />
          ) : (
            <BuyViaWppCard name={name}  />
          )
        ) : (
          <>
            {showPlanSelection && (
              <Grid container spacing={2}>
                {(() => {
                const base = [
                  { value: 'oneTime' as const, title: text[0], subtitle: text[1] },
                  { value: 'installment' as const, title: text[2], subtitle: text[3] }
                ]
                const opts = mdDown ? [...base].reverse() : base
                return opts.map((opt) => (
                  <Grid item xs={12} sm={6} key={opt.value}>
                    <Grid
                      container
                      className={paymentOption === opt.value ? 'paymentCard activated' : 'paymentCard disabled'}
                      onClick={() => handlePaymentOptionChange(opt.value)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderRadius: '12px',
                        p: { xs: '16px', md: '10px' }
                      }}
                    >
                      <Grid item className="cardCheckbox" sx={{ mr: 1 }}>
                        <CheckboxDefault checked={paymentOption === opt.value} checkedIcon={<Checked />} uncheckedIcon={<Unchecked />} />
                      </Grid>
                      <Grid item sx={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
                        <Typography variant="h6" sx={{ fontSize: { xs: 16 } }}>{opt.title}</Typography>
                        <Typography variant="body2" sx={{ fontSize: { xs: 12 } }}>{opt.subtitle}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                ))})()}
              </Grid>
            )}

            {showInstallmentSummary && (
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={8}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">{text[4]}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {text[5]}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}

            <Collapse in={paymentOption === 'installment'}>
              {(isAnnual && !paymentStep && paymentOption === 'installment') && (
                <>
                  <Divider sx={{ mb: 2.5 }} />
                  <Typography variant="h6">Avance para o próximo passo</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Clique em "continuar" para continuar com a compra parcelada.</Typography>
                </>
              )}
            </Collapse>

            <Collapse in={paymentOption === 'oneTime'}>
              {(isAnnual && !paymentStep && paymentOption === 'oneTime') && <Divider sx={{ mb: 2.5 }} />}
              <PaymentMethods text={text.slice(6)} paymentStep={paymentStep} noStock={noStock} />
            </Collapse>
          </>
        )}
      </Grid>
    </Grid>
  )
}